import React, { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar';

import type { AIPlugin } from '../../aiPluginType';
import { createOpenAIModalCommand } from '../../pm-plugins/decoration/actions';
import type { EditorPluginAIProvider } from '../../types/types';

import type { BlockMenuActionDef } from './types';

/**
 * Resolves the click handler for a block menu action.
 *
 * - Custom click actions (`onClick`) are invoked directly.
 * - Config item actions open the AI modal via `createOpenAIModalCommand`.
 * - Parent actions (with `children`) have no click behaviour.
 */
const executeAction = ({
	action,
	editorView,
	editorPluginAIProvider,
	locale,
}: {
	action: BlockMenuActionDef;
	editorPluginAIProvider: EditorPluginAIProvider;
	editorView: EditorView | undefined;
	locale: string;
}): boolean => {
	if (action.onClick) {
		action.onClick();
		return true;
	}

	if (editorView && action.configItem) {
		const openAIModalCommand = createOpenAIModalCommand({
			state: editorView.state,
			configItem: action.configItem,
			lastTriggeredFrom: 'blockMenu',
			triggerMethod: 'click',
			aiGlobalOptIn: editorPluginAIProvider.aiGlobalOptIn,
			locale,
		});

		openAIModalCommand(editorView.state, editorView.dispatch);
		return true;
	}

	return false;
};

export const BlockMenuActionItem = ({
	action,
	api,
	editorPluginAIProvider,
	getEditorView,
}: {
	action: BlockMenuActionDef;
	api: ExtractInjectionAPI<AIPlugin>;
	editorPluginAIProvider: EditorPluginAIProvider;
	getEditorView: () => EditorView | undefined;
}): React.JSX.Element => {
	const { formatMessage, locale } = useIntl();

	const handleClick = useCallback(() => {
		const didExecute = executeAction({
			action,
			editorView: getEditorView(),
			editorPluginAIProvider,
			locale,
		});

		if (didExecute) {
			// Close the block menu (defensive - block controls are optional)
			api?.core?.actions.execute(
				api?.blockControls?.commands?.toggleBlockMenu({ closeMenu: true }),
			);
		}
	}, [action, api, editorPluginAIProvider, getEditorView, locale]);

	return (
		<ToolbarDropdownItem elemBefore={action.elemBefore} onClick={handleClick}>
			{formatMessage(action.label)}
		</ToolbarDropdownItem>
	);
};
